import { useCallback, useRef, useState } from 'react';
import TurnstileField from '../../components/TurnstileField';
import { isLocalDev } from '../../lib/isLocalDev';
import { submitLandingTestForm } from '../../lib/submitLandingTestForm';
import {
  LANDING_DESCRIPTION_MAX,
  validateLandingTestForm,
} from '../../lib/validateLandingTestForm';
import './LandingTestPage.css';

const INITIAL = {
  name: '',
  email: '',
  phone: '',
  service: '',
  budget: '',
  description: '',
};

const LandingTestPage = () => {
  const [fields, setFields] = useState(INITIAL);
  const [fieldErrors, setFieldErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileRequired, setTurnstileRequired] = useState(!isLocalDev());
  const turnstileRef = useRef(null);

  const handleTurnstileReady = useCallback((isAvailable) => {
    setTurnstileRequired(isAvailable);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === 'description' ? value.slice(0, LANDING_DESCRIPTION_MAX) : value;

    setFields((prev) => ({ ...prev, [name]: nextValue }));

    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }

    if (status === 'error') {
      setStatus('idle');
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const honey = e.currentTarget.elements._honey?.value;
    if (honey) return;

    const validation = validateLandingTestForm(fields);
    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      setStatus('error');
      setError('Completa tutti i campi obbligatori prima di inviare.');
      return;
    }

    if (turnstileRequired && !turnstileToken) {
      setStatus('error');
      setError('Completa la verifica di sicurezza prima di inviare.');
      return;
    }

    if (isLocalDev()) {
      setStatus('error');
      setError('Il form può essere inviato solo sul sito live. Apri andreafagioli.com/landingtest.');
      return;
    }

    setFieldErrors({});
    setStatus('sending');
    setError('');

    try {
      await submitLandingTestForm({
        ...validation.values,
        turnstileToken,
        honey,
      });
      setFields(INITIAL);
      setTurnstileToken('');
      turnstileRef.current?.reset();
      setStatus('success');
    } catch (err) {
      turnstileRef.current?.reset();
      setTurnstileToken('');
      setStatus('error');
      setError(err.message || 'Impossibile inviare la richiesta. Riprova.');
    }
  };

  const isSending = status === 'sending';

  return (
    <main className="landing-test-page" data-testid="landing-test-page">
      <div className="landing-test-ambient" aria-hidden="true" />

      <div className="landing-test-panel">
        <div className="landing-test-panel-border" aria-hidden="true" />
        <div className="landing-test-panel-inner">
          <header className="landing-test-header">
            <p className="landing-test-eyebrow">Private test</p>
            <h1 className="landing-test-title">Richiedi un preventivo</h1>
            <p className="landing-test-lead">
              Compila il form con i dettagli del progetto. Ti rispondo entro 24–48 ore lavorative.
            </p>
          </header>

          {status === 'success' ? (
            <div className="landing-test-success" role="status">
              <p className="landing-test-success-title">Richiesta inviata.</p>
              <p className="landing-test-success-copy">
                Grazie. Ho ricevuto i tuoi dati e ti ricontatterò a breve.
              </p>
            </div>
          ) : (
            <form className="landing-test-form" onSubmit={handleSubmit} noValidate>
              <label className="landing-test-honey" aria-hidden="true">
                <span>Leave empty</span>
                <input type="text" name="_honey" tabIndex={-1} autoComplete="off" />
              </label>

              <div className="landing-test-field">
                <label htmlFor="landing-name" className="landing-test-label">
                  Nome <span className="landing-test-required" aria-hidden="true">*</span>
                </label>
                <input
                  id="landing-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  value={fields.name}
                  onChange={handleChange}
                  className={fieldErrors.name ? 'is-error' : ''}
                  disabled={isSending}
                />
                {fieldErrors.name ? (
                  <p className="landing-test-error">{fieldErrors.name}</p>
                ) : null}
              </div>

              <div className="landing-test-field">
                <label htmlFor="landing-email" className="landing-test-label">
                  Email <span className="landing-test-required" aria-hidden="true">*</span>
                </label>
                <input
                  id="landing-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={fields.email}
                  onChange={handleChange}
                  className={fieldErrors.email ? 'is-error' : ''}
                  disabled={isSending}
                />
                {fieldErrors.email ? (
                  <p className="landing-test-error">{fieldErrors.email}</p>
                ) : null}
              </div>

              <div className="landing-test-field">
                <label htmlFor="landing-phone" className="landing-test-label">
                  Telefono <span className="landing-test-required" aria-hidden="true">*</span>
                </label>
                <input
                  id="landing-phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  value={fields.phone}
                  onChange={handleChange}
                  className={fieldErrors.phone ? 'is-error' : ''}
                  disabled={isSending}
                />
                {fieldErrors.phone ? (
                  <p className="landing-test-error">{fieldErrors.phone}</p>
                ) : null}
              </div>

              <div className="landing-test-field">
                <label htmlFor="landing-service" className="landing-test-label">
                  Servizio richiesto <span className="landing-test-required" aria-hidden="true">*</span>
                </label>
                <input
                  id="landing-service"
                  name="service"
                  type="text"
                  value={fields.service}
                  onChange={handleChange}
                  placeholder="Es. landing page, sito web, campagna ads"
                  className={fieldErrors.service ? 'is-error' : ''}
                  disabled={isSending}
                />
                {fieldErrors.service ? (
                  <p className="landing-test-error">{fieldErrors.service}</p>
                ) : null}
              </div>

              <div className="landing-test-field">
                <label htmlFor="landing-budget" className="landing-test-label">
                  Budget <span className="landing-test-required" aria-hidden="true">*</span>
                </label>
                <input
                  id="landing-budget"
                  name="budget"
                  type="text"
                  value={fields.budget}
                  onChange={handleChange}
                  placeholder="Es. €2.000 – €5.000"
                  className={fieldErrors.budget ? 'is-error' : ''}
                  disabled={isSending}
                />
                {fieldErrors.budget ? (
                  <p className="landing-test-error">{fieldErrors.budget}</p>
                ) : null}
              </div>

              <div className="landing-test-field">
                <label htmlFor="landing-description" className="landing-test-label">
                  Descrizione richiesta{' '}
                  <span className="landing-test-required" aria-hidden="true">*</span>
                </label>
                <textarea
                  id="landing-description"
                  name="description"
                  rows={5}
                  value={fields.description}
                  onChange={handleChange}
                  className={fieldErrors.description ? 'is-error' : ''}
                  disabled={isSending}
                />
                <p className="landing-test-counter" aria-live="polite">
                  {fields.description.length}/{LANDING_DESCRIPTION_MAX}
                </p>
                {fieldErrors.description ? (
                  <p className="landing-test-error">{fieldErrors.description}</p>
                ) : null}
              </div>

              <TurnstileField
                ref={turnstileRef}
                onToken={setTurnstileToken}
                onExpire={() => setTurnstileToken('')}
                onReady={handleTurnstileReady}
              />

              {isLocalDev() ? (
                <p className="landing-test-dev-note">
                  Verifica di sicurezza disattivata in locale. Invia dal sito live.
                </p>
              ) : null}

              {error ? (
                <p className="landing-test-form-error" role="alert">
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                className="landing-test-submit"
                disabled={isSending}
              >
                {isSending ? 'Invio in corso…' : 'Invia richiesta'}
              </button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
};

export default LandingTestPage;
